import { useState } from 'react';
import { Button, Card, Drawer, Popconfirm } from 'antd';
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { ButtonLZ } from '../../design/Buttons/ButtonLZ';
import './CompanyIAGuide.css';
import { authFetch } from '../../../../common/request/authFetch';
import { useAuth } from '../../../../store/AuthContext/AuthContext';
import { useFetch } from '../../../../hooks/useFetch/useFetch';
import IAGuideModal from './modal/IAGuideModal';
import { IADefaultLanguage } from './defaultLanguage/IADefaultLanguage';
import TableLZM from '../../design/table/TableLZM';

const mockData = [
	{
		caseToAnswer: 'Reseña con puntuación 4 o 5 y sin texto',
		answer: 'Agradecer la reseña',
	},
	{
		caseToAnswer: 'Reseña con puntuación 1 o 2 y texto quejándose del servicio',
		answer: 'Sugerir que envíen un email a servicio@nombreempresa.net',
	},
	{
		caseToAnswer: 'Para cualquier puntuación y texto',
		answer: 'Firmar con: NombreDeEmpresa. Dejando una linea antes de la firma',
	},
];

const sectionLabelStyle = {
	fontSize: 11,
	fontWeight: 600 as const,
	color: '#475569',
	letterSpacing: '0.03em',
	textTransform: 'uppercase' as const,
	paddingLeft: 2,
};

const cardStyle = {
	background: '#fff',
	margin: 0,
	marginLeft: 0,
	width: '100%',
	borderRadius: 10,
	border: '1px solid rgba(148, 163, 184, 0.28)',
};

export const CompanyIAGuide = (props: { locationId?: any; onTestClick?: () => void }) => {

	const locationIdPath = props.locationId ? '/' + props.locationId : '';
	const { projectGroup } = useAuth();

	const [openModal, setOpenModal] = useState(false);
	const [defaultLanguage, setDefaultLanguage] = useState();
	const [guides, setGuides] = useState<any[]>([]);
	const [updateData, setUpdateData] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isExample, setIsExample] = useState(false);

	useFetch(`${process.env.REACT_APP_API}/projects/${projectGroup.project}/${projectGroup.group}/ia/guide${locationIdPath}`, {}, [updateData], (data) => {
		setDefaultLanguage(data.defaultLanguage);
		setGuides(data.guides || []);
		setIsLoading(data.isLoading);
	});

	const handleDelete = async (record: any) => {
		await authFetch(
			`${process.env.REACT_APP_API}/projects/${projectGroup.project}/${projectGroup.group}/ia/guide`,
			{ method: 'DELETE', data: record }
		).then(() => { setUpdateData(!updateData); });
	};

	const exampleColumns = [
		{
			title: 'Caso',
			dataIndex: 'caseToAnswer',
			render: (text: any) => <span style={{ color: 'grey' }}>{text}</span>,
		},
		{
			title: 'Instrucción',
			dataIndex: 'answer',
			render: (text: any) => <span style={{ color: 'grey' }}>{text}</span>,
		},
	];

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
			<section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
				<div style={sectionLabelStyle}>Idioma por defecto</div>
				{defaultLanguage && (
					<IADefaultLanguage
						language={defaultLanguage}
						groupAccount={projectGroup.group}
						locationId={props.locationId}
					/>
				)}
			</section>

			<section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
				<div style={sectionLabelStyle}>Lista de instrucciones</div>
				<Card
					className='card-layout'
					bodyStyle={{ padding: '14px 16px' }}
					style={cardStyle}
					loading={isLoading}
				>
					<div style={{
						display: 'flex',
						alignItems: 'flex-start',
						justifyContent: 'space-between',
						gap: 12,
						padding: '10px 12px',
						marginBottom: 14,
						borderRadius: 8,
						background: '#f8fafc',
						border: '1px solid rgba(148, 163, 184, 0.22)',
					}}>
						<div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0 }}>
							<InfoCircleOutlined style={{ color: 'teal', marginTop: 2, flexShrink: 0 }} />
							<small style={{ color: '#64748b', lineHeight: 1.45 }}>
								Define en qué casos debe responder la IA y qué instrucción seguir en cada situación, <b>ten en cuenta que la IA ya tiene configurada info del local por defecto (nombre, categoría y descripción de la ficha)</b>
							</small>
						</div>
						{props.onTestClick && (
							<ButtonLZ
								size='small'
								type='ghost'
								onClick={props.onTestClick}
								style={{
									fontSize: 12,
									border: '1px solid #e9d5ff',
									background: '#faf5ff',
									color: '#7e22ce',
									height: 30,
									padding: '0 10px',
									flexShrink: 0,
								}}
							>
								<span style={{ display: 'flex', alignItems: 'center', gap: 5, fontWeight: 600 }}>
									<span className="material-symbols-outlined" style={{ fontSize: 15, color: '#7e22ce' }}>auto_awesome</span>
									Pruébame
								</span>
							</ButtonLZ>
						)}
					</div>

					{guides.length > 0 && (
						<div style={{
							display: 'grid',
							gridTemplateColumns: 'minmax(120px, 1.2fr) 1.5fr auto',
							gap: 10,
							padding: '0 12px 6px',
						}}>
							<span style={{
								fontSize: 11,
								fontWeight: 600,
								color: '#94a3b8',
								letterSpacing: '0.03em',
								textTransform: 'uppercase',
							}}>
								Caso
							</span>
							<span style={{
								fontSize: 11,
								fontWeight: 600,
								color: '#94a3b8',
								letterSpacing: '0.03em',
								textTransform: 'uppercase',
							}}>
								Instrucción
							</span>
							<span />
						</div>
					)}

					<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
						{guides.map((row: any, index: number) => (
							<div
								key={row.id || `${row.caseToAnswer}-${index}`}
								style={{
									display: 'grid',
									gridTemplateColumns: 'minmax(120px, 1.2fr) 1.5fr auto',
									alignItems: 'center',
									gap: 10,
									padding: '10px 12px',
									borderRadius: 8,
									border: '1px solid rgba(148, 163, 184, 0.22)',
									background: '#fff',
								}}
							>
								<div style={{ minWidth: 0 }}>
									<small style={{ color: '#334155', lineHeight: 1.4, fontWeight: 600 }}>
										{row.caseToAnswer}
									</small>
								</div>
								<div style={{ textAlign: 'left', minWidth: 0 }}>
									<small style={{ color: '#334155', lineHeight: 1.4 }}>
										{row.answer}
									</small>
								</div>
								<Popconfirm title="Confirmar eliminación" onConfirm={() => handleDelete(row)}>
									<Button
										shape='round'
										size='small'
										type='link'
										style={{ color: '#94a3b8', justifySelf: 'end', paddingInline: 4 }}
									>
										<DeleteOutlined style={{ fontSize: 12 }} />
										<small> Eliminar</small>
									</Button>
								</Popconfirm>
							</div>
						))}
					</div>

					<div style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: 10,
						marginTop: 16,
					}}>
						<a onClick={() => { setIsExample(true); }} style={{ color: '#64748b', fontSize: 13 }}>
							Ver un ejemplo
						</a>
						<ButtonLZ onClick={() => { setOpenModal(true); }}>
							<PlusOutlined /> Añadir instrucción
						</ButtonLZ>
					</div>
				</Card>
			</section>

			{openModal && (
				<IAGuideModal
					locationId={props.locationId}
					visible={openModal}
					onFinish={() => { setOpenModal(false); }}
					setUpdateData={setUpdateData}
					updateData={updateData}
				/>
			)}

			{isExample && (
				<Drawer
					width={'40%'}
					title={<b style={{ color: 'lightslategray' }}>Ejemplo de instrucciones</b>}
					open={isExample}
					onClose={() => { setIsExample(false); }}
				>
					<p style={{ color: 'grey' }}>
						Este es un ejemplo que puedes usar como referencia para crear tus propias instrucciones
					</p>
					<TableLZM
						size='small'
						dataSource={mockData}
						columns={exampleColumns}
						loading={false}
						style={{ color: 'grey' }}
					/>
				</Drawer>
			)}
		</div>
	);
};
